import { getKnowledgeUploadSupportCopy } from "@/lib/knowledge-validation";

export interface SupportKnowledgeEntry {
  id: string;
  patterns: Array<string | RegExp>;
  answer: string;
}

const SUPPORT_HELP_ASSISTANT_NAMES = new Set([
  "aditya labs sales agent",
  "aditya labs ai helper",
]);

const SUPPORT_HELP_ASSISTANT_IDS = new Set([
  "support-widget",
  "ask-ai-panel",
]);

export const SUPPORT_FALLBACK_REPLY =
  "I don’t have that specific information in this assistant’s knowledge base. I can help with Aditya Labs product questions, pricing access, setup, or request-access guidance.";

const ADITYA_LABS_TOPIC_PATTERNS: RegExp[] = [
  /\baditya labs\b/i,
  /\baditya\b/i,
  /\bwhat do you do\b/i,
  /\bindustries you serve\b/i,
  /\bwhat industries do you serve\b/i,
  /\bagent\b/i,
  /\bwidget\b/i,
  /\bdeploy\b/i,
  /\bknowledge base\b/i,
  /\brag\b/i,
  /\bpricing\b/i,
  /\bprice\b/i,
  /\bcost\b/i,
  /\bplan\b/i,
  /\btrial\b/i,
  /\brequest access\b/i,
  /\bsign up\b/i,
  /\bsignup\b/i,
  /\bpublic signup\b/i,
  /\bfree plan\b/i,
  /\bfree trial\b/i,
  /\bcontact sales\b/i,
  /\bpricing page\b/i,
  /\bbilling\b/i,
  /\bintegrations\b/i,
  /\bwebhook\b/i,
  /\bcalendar\b/i,
  /\bemail\b/i,
  /\bvoice\b/i,
  /\bleads?\b/i,
  /\banalytics\b/i,
  /\bcompliance\b/i,
  /\bsettings\b/i,
  /\bteam\b/i,
  /\bdashboard\b/i,
];

export const SUPPORT_KNOWLEDGE: SupportKnowledgeEntry[] = [
  {
    id: "product-overview",
    patterns: [
      /\bwhat is aditya labs\b/i,
      /\bwhat does aditya labs do\b/i,
      /\bhow does aditya labs work\b/i,
      /\bhow does it work\b/i,
    ],
    answer: `Aditya Labs helps small businesses create AI agents for routine customer questions, lead qualification, knowledge-base answers, website chat, and customer handoff.

It is AI, not a human, and human oversight still matters before public use.

If public signup is paused in your workspace, request access or contact sales. Existing users can sign in.`,
  },
  {
    id: "create-agent",
    patterns: [
      /\bhow do i create\b/i,
      /\bcreate an ai agent\b/i,
      /\bnew agent\b/i,
      /\bset up ai assistant\b/i,
      /\bhow do i make an agent\b/i,
    ],
    answer: `1. Sign in.
2. Open Agents from the sidebar.
3. Click New Agent or Set Up AI Assistant.
4. Choose the business type or industry.
5. Enter the business name and details.
6. Add knowledge using text, files, or website import where available.
7. Launch or create the agent.
8. Test it in Chat Preview.
9. Set status to Active before public deployment.`,
  },
  {
    id: "knowledge-base",
    patterns: [
      /\bknowledge base\b/i,
      /\brag\b/i,
      /\bembedding\b/i,
      /\bgeneric answer\b/i,
      /\bdoes not know\b/i,
      /\bsource indicators?\b/i,
      /\brag badges?\b/i,
      /\bwhy does my agent not answer\b/i,
      /\bnot answer from my knowledge base\b/i,
    ],
    answer: `The knowledge base is what the agent uses to answer business-specific questions.

Add business hours, pricing, services, policies, FAQs, menus, location, booking rules, cancellation rules, and handoff instructions.

If an answer is weak or generic, add clearer entries, then test again in Chat Preview and check whether embeddings have finished.

Where source indicators or RAG badges are shown, treat them as evidence that the answer used knowledge-base content.

Avoid uploading private patient, customer, financial, or confidential data unless the workspace privacy and compliance settings are understood and appropriate.`,
  },
  {
    id: "upload-documents",
    patterns: [
      /\bupload documents?\b/i,
      /\bupload file\b/i,
      /\bhow do i upload\b/i,
      /\bdocuments?\s*\?/i,
    ],
    answer: `1. Open the Knowledge Base area.
2. Click Add Knowledge or Upload File.
3. ${getKnowledgeUploadSupportCopy()}
4. Save the upload.
5. Wait for Embedding in progress or a similar status to finish before expecting the answer to improve.

If the upload still fails, double-check the file type, file size, and whether the target agent is selected correctly.`,
  },
  {
    id: "website-import",
    patterns: [
      /\bimport from website\b/i,
      /\bwebsite import\b/i,
      /\bimport knowledge from my website\b/i,
      /\bscrape\b/i,
      /\bimport from my url\b/i,
    ],
    answer: `If Import from Website exists in your workspace, open the Knowledge Base import area and paste the public website URL.

It works best on text-heavy pages such as Services, Pricing, FAQs, opening hours, and policies.
It may not work well on image-only pages, private pages, PDFs behind logins, or pages blocked from scraping.

Always review imported content before trusting the answers.`,
  },
  {
    id: "deploy-website",
    patterns: [
      /\bdeploy\b/i,
      /\binstall\b/i,
      /\bembed\b/i,
      /\bwebsite widget\b/i,
      /\bhow can i deploy\b/i,
      /\bhow do i deploy\b/i,
    ],
    answer: `1. Sign in to Aditya Labs.
2. Open Agents.
3. Select the agent you want to deploy.
4. Open the Deploy tab.
5. Find the Website Chat Widget section.
6. Copy the full embed code snippet.
7. Paste it before the closing </body> tag, or into the website builder's custom code or header/footer area.
8. Make sure the agent is Active before public use.
9. Save or publish the website.
10. Open the website in a private or incognito browser and send a test message.

Platform notes:
- WordPress: use a trusted header/footer code plugin or the theme custom code area.
- Shopify: add the snippet to theme.liquid before </body>, or use a custom code app.
- Webflow: Project Settings > Custom Code > Footer Code.
- Wix and Squarespace: use the custom embed or custom code area.
- Custom HTML site: paste the snippet before </body>.
- If unsure, ask the website developer to add the snippet.

Troubleshooting if the widget does not appear:
- Confirm the script was pasted on the published site, not only in editor preview.
- Confirm the agentId in the snippet matches the intended agent.
- Confirm the agent is Active.
- Clear cache or test in incognito.
- Check browser Console and Network tabs for widget.js or chat request errors.
- Confirm the domain allows the widget if there is any domain restriction logic.`,
  },
  {
    id: "active-agent",
    patterns: [
      /\bactive agent\b/i,
      /\bmake.*active\b/i,
      /\bdraft\b/i,
      /\bpublic deployment\b/i,
    ],
    answer: `Draft agents are for setup and testing.
Active agents are intended for public use.

Before embedding on a live website, verify the agent is Active and test common customer questions in Chat Preview.`,
  },
  {
    id: "preview-vs-widget",
    patterns: [
      /\bchat preview\b/i,
      /\bwebsite widget\b/i,
      /\bsource indicators?\b/i,
      /\brag badges?\b/i,
      /\btest both\b/i,
    ],
    answer: `Chat Preview is for testing inside the dashboard.
Website Widget is what public visitors see after deployment.

Users should test both before launch.
Answer quality depends on the agent's knowledge base.
Where source indicators or RAG badges are shown, use them as evidence that the reply was grounded in knowledge-base content.`,
  },
  {
    id: "integrations",
    patterns: [
      /\bapi access\b/i,
      /\bemail automation\b/i,
      /\btelephone\b/i,
      /\bvoice\b/i,
      /\bphone\b/i,
      /\bintegrations\b/i,
    ],
    answer: `Website widget is the easiest integration path.

API access may be available for technical integrations if credentials and routes are configured.
Email automation may require mailbox or domain setup and testing.
Telephone or voice is more setup-heavy and should be treated as advanced or private-preview unless the current workspace clearly supports it.

I will not claim a channel is fully available unless the current product UI and code support it.`,
  },
  {
    id: "troubleshooting",
    patterns: [
      /\bwidget does not answer\b/i,
      /\bwidget says it does not know\b/i,
      /\bgeneric answer\b/i,
      /\bupload file failed\b/i,
      /\bknowledge base shows embedding\b/i,
      /\bdeleted demo agents came back\b/i,
      /\breappear\b/i,
      /\bhow do i delete agents\b/i,
      /\bcontact a human\b/i,
      /\bhuman handoff\b/i,
    ],
    answer: `- The widget does not answer: confirm the agent is Active, the snippet is on the published site, and the browser Console or Network tabs do not show widget.js or chat request errors.
- The widget says it does not know: add clearer knowledge entries, wait for embedding or RAG to finish, and re-test in Chat Preview.
- Upload File failed: confirm the file type is supported, and for legacy DOC files try saving as DOCX before retrying. Legacy DOC is accepted as best-effort; DOCX is recommended for best accuracy.
- Knowledge base shows embedding: that usually means background processing is still running; wait and refresh.
- My agent gives a generic answer: add more specific knowledge base content and test the exact customer questions.
- Deleted demo agents came back: delete them from Agents, refresh, and check whether any demo or seed automation is still restoring them.
- How do I delete agents?: open Agents, select the agent, and delete it from the actions menu or bulk-select controls.
- How do I contact the team?: use Contact Us in the widget.`,
  },
  {
    id: "request-access",
    patterns: [
      /\brequest access\b/i,
      /\bprivate preview\b/i,
      /\bsign up\b/i,
      /\bsignup\b/i,
      /\bhow do i get access\b/i,
      /\bget access\b/i,
      /\bwhat does it cost\b/i,
      /\bhow much does it cost\b/i,
      /\bhow much is it\b/i,
      /\bwhat are your prices\b/i,
      /\bpricing\b/i,
      /\bprice\b/i,
      /\bcost\b/i,
      /\bfree plan\b/i,
      /\bfree trial\b/i,
      /\bcontact sales\b/i,
      /\bplans?\b/i,
      /\bsubscription\b/i,
      /\bpricing plan\b/i,
    ],
    answer: `Aditya Labs is currently in private preview.

Public self-serve signup is paused while we finish launch verification. Existing users can sign in normally.

If you are new, please use Request Access or Contact Sales. We will share current plan availability and pricing during the access review.

You can also check the Pricing page for the latest public information.`,
  },
  {
    id: "safety",
    patterns: [
      /\btoothache\b/i,
      /\bdiagnose\b/i,
      /\bdiagnosis\b/i,
      /\bmedical\b/i,
      /\blegal\b/i,
      /\bfinancial\b/i,
    ],
    answer: `I cannot diagnose a toothache or replace a qualified professional.

Please contact a qualified dental professional. If you have severe swelling, trouble breathing, trouble swallowing, or uncontrolled bleeding, seek urgent or emergency care now.`,
  },
];

function patternMatches(message: string, pattern: string | RegExp): boolean {
  if (typeof pattern === "string") {
    return message.includes(pattern.toLowerCase());
  }
  return pattern.test(message);
}

export function getSupportKnowledgeEntries(): SupportKnowledgeEntry[] {
  return SUPPORT_KNOWLEDGE.map((entry) => ({ ...entry }));
}

export function isSupportHelpAssistant(agentName?: string | null, agentId?: string | null): boolean {
  const normalizedName = agentName?.trim().toLowerCase();
  const normalizedId = agentId?.trim().toLowerCase();
  return Boolean(
    (normalizedName && SUPPORT_HELP_ASSISTANT_NAMES.has(normalizedName)) ||
    (normalizedId && SUPPORT_HELP_ASSISTANT_IDS.has(normalizedId))
  );
}

export function getSupportHelpReply(message: string): string | null {
  const normalized = message.toLowerCase();

  for (const entry of SUPPORT_KNOWLEDGE) {
    if (entry.patterns.some((pattern) => patternMatches(normalized, pattern))) {
      return entry.answer;
    }
  }

  return null;
}

export function isAdityaLabsSupportQuestion(message: string): boolean {
  const normalized = message.toLowerCase();
  return ADITYA_LABS_TOPIC_PATTERNS.some((pattern) => pattern.test(normalized));
}

export function getSupportFallbackReply(message: string): string | null {
  if (getSupportHelpReply(message)) {
    return null;
  }

  if (isAdityaLabsSupportQuestion(message)) {
    return null;
  }

  return SUPPORT_FALLBACK_REPLY;
}
